import { useContext, useState } from 'react';
import styled from 'styled-components';
import useSWR from 'swr';
import { BILLING_ALERT_MAX_THRESHOLD, BILLING_ALERT_PRODUCT_NAMES, BillingAlert, BillingAlertProduct, formatCurrencyAmount, getCurrencyDecimals, getCurrencySymbol } from '../../../../../../../../../typings/BillingAlert.interface';
import APIMethodKeys from '../../../../../../client/APIMethodKeys';
import Button from '../../../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../../../common/base/Card';
import Icon from '../../../../../common/base/Icon';
import Loading from '../../../../../common/base/Loading';
import Text from '../../../../../common/base/Text';
import Tooltip from '../../../../../common/base/Tooltip';
import Input from '../../../../../common/Form/Fields/Input/Input';
import Switch from '../../../../../common/Form/Fields/Switch';
import { Div } from '../../../../../common/helpers/StyledUtils';
import { useToasts } from '../../../../../common/Toast';
import { GlobalContext } from '../../../../../contexts/GlobalContext';
import { DashboardContext } from '../../../DashboardContext';
const StyledAlertRow = styled(StyledCardSection)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({
  theme
}) => theme.spacing(4)};

  @media (max-width: 640px) {
    align-items: stretch;
    flex-direction: column;
  }
`;
const StyledAmount = styled(Input)`
  width: 180px;
  flex-shrink: 0;

  input {
    padding-left: ${({
  theme
}) => theme.spacing(7)} !important;
    padding-right: ${({
  theme
}) => theme.spacing(11)} !important;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  @media (max-width: 640px) {
    width: 100%;
  }
`;

// The saved threshold, kept while the alert is off so turning it back on restores it.
const savedThreshold = (alert?: BillingAlert) => alert?.threshold !== null && alert?.threshold !== undefined ? alert.threshold.toFixed(getCurrencyDecimals(alert.currency)) : '';

// Returns an error message when the amount isn't valid in the alert's currency.
const validateThreshold = (value: string, currency: string) => {
  const decimals = getCurrencyDecimals(currency);
  const pattern = decimals > 0 ? new RegExp(`^\\d+(\\.\\d{1,${decimals}})?$`) : /^\d+$/;
  // Divide rather than multiply by fractions so 0.01 and 999999999.99 stay exact.
  const unit = 1 / 10 ** decimals;
  const max = Math.floor(BILLING_ALERT_MAX_THRESHOLD * 10 ** decimals) / 10 ** decimals;
  if (pattern.test(value) && Number(value) >= unit && Number(value) <= max) return undefined;
  return `Enter an amount from ${formatCurrencyAmount(unit, currency)} to ${formatCurrencyAmount(max, currency)}${decimals > 0 ? `, with up to ${decimals} decimals` : ', without decimals'}.`;
};
const BillingAlerts: React.FC<{
  product: BillingAlertProduct;
}> = ({
  product
}) => {
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    organization_role
  } = useContext(DashboardContext);
  const {
    addToast
  } = useToasts();
  const can_edit = organization_role === 'owner' || organization_role === 'admin';
  const {
    data: alert,
    error: error_0,
    mutate
  } = useSWR(APIMethodKeys.billing.getAlert(product), () => HookdeckAPI.billing.getAlert(product),
  // Client errors (e.g. no subscription) are permanent; only retry server errors.
  {
    shouldRetryOnError: error => !error.status || error.status >= 500
  });
  const [enabled_draft, setEnabledDraft] = useState<boolean>();
  const [threshold_draft, setThresholdDraft] = useState<string>();
  const [save_error, setSaveError] = useState<string>();
  const [is_saving, setSaving] = useState(false);
  const is_ready = !!alert && !error_0;
  const is_enabled = enabled_draft ?? alert?.enabled ?? false;
  const threshold = threshold_draft ?? savedThreshold(alert);
  const is_changed = is_ready && (is_enabled !== alert.enabled || is_enabled && Number(threshold) !== Number(savedThreshold(alert)));
  const save = async () => {
    if (!can_edit || is_saving || !is_changed || !alert) return;
    const value = threshold.trim();
    const validation_error = is_enabled && validateThreshold(value, alert.currency);
    if (validation_error) {
      setSaveError(validation_error);
      return;
    }
    setSaving(true);
    try {
      const updated = await HookdeckAPI.billing.setAlert(product, is_enabled ? Number(value) : null);
      await mutate(updated, false);
      // Fields stay editable while saving; keep any edit made after Save was clicked.
      setEnabledDraft(draft => draft === enabled_draft ? undefined : draft);
      setThresholdDraft(draft_0 => draft_0 === threshold_draft ? undefined : draft_0);
      setSaveError(undefined);
      addToast('success', is_enabled ? 'Usage alert saved' : 'Usage alert turned off');
    } catch (error_1: any) {
      setSaveError(error_1?.response?.message || 'Could not save this alert. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  const product_name = BILLING_ALERT_PRODUCT_NAMES[product];
  const field_id = `${product}_threshold`;
  const error_id = `${field_id}-error`;
  const load_error = error_0?.status === 404 ? `Available when you activate ${product_name}.` : error_0 && error_0.status < 500 && error_0.response?.message;
  return <Div m={{
    b: 10
  }}>
      <Text bold size="l" as="h2" m={0}>
        Usage alert
      </Text>
      <Text muted as="p" m={{
      b: 4
    }}>
        Email organization owners and admins when {product_name} usage charges reach a threshold.
      </Text>
      <form noValidate onSubmit={event => {
      event.preventDefault();
      void save();
    }}>
        <StyledCard>
          <StyledCardSection p={{
          x: 4,
          y: 3
        }} flex={{
          align: 'center',
          gap: 3
        }}>
            {!alert && !error_0 ? <Loading /> : <Tooltip tooltip="Only organization owners and admins can change usage alerts." disabled={can_edit || !is_ready}>
                <Switch checked={is_enabled} disabled={!can_edit || !is_ready} onChange={event_0 => {
              setEnabledDraft(event_0.target.checked);
              setSaveError(undefined);
            }} />
              </Tooltip>}
            <Div flex={{
            align: 'center',
            gap: 1
          }}>
              <Text>Enable alert</Text>
              <Tooltip tooltip="Thresholds apply to usage charges per billing period, before prepaid credits, invoice discounts, and taxes. Base fees are excluded. Usage can take up to an hour to reach billing, so an alert may arrive after the threshold is crossed. Event processing continues after an alert.">
                <Icon muted icon="info" />
              </Tooltip>
            </Div>
          </StyledCardSection>
          {error_0 && <StyledCardSection p={{
          x: 4,
          y: 3
        }}>
              {load_error ? <Text muted={error_0.status === 404} danger={error_0.status !== 404} size="s">
                  {load_error}
                </Text> : <Text danger size="s">
                  Could not load this alert.{' '}
                  <Button ghost small onClick={() => void mutate()}>
                    Retry
                  </Button>
                </Text>}
            </StyledCardSection>}
          {is_ready && is_enabled && <StyledAlertRow p={4}>
              <Div>
                <Text as="label" htmlFor={field_id} semi>
                  Threshold{' '}
                  <Text as="span" muted>
                    (per billing period)
                  </Text>
                </Text>
              </Div>
              <StyledAmount inputContainer={children => <Div style={{
            position: 'relative'
          }}>
                    <Text muted as="span" style={{
              position: 'absolute',
              left: 12,
              top: 7,
              pointerEvents: 'none'
            }}>
                      {getCurrencySymbol(alert.currency)}
                    </Text>
                    {children}
                    <Text muted as="span" size="xs" style={{
              position: 'absolute',
              right: 12,
              top: 8,
              pointerEvents: 'none'
            }}>
                      {alert.currency}
                    </Text>
                  </Div>}>
                <input id={field_id} name={field_id} type="text" inputMode="decimal" placeholder="0.00" aria-describedby={save_error ? error_id : undefined} aria-invalid={!!save_error} disabled={!can_edit} value={threshold} onChange={event_1 => {
              setThresholdDraft(event_1.target.value);
              setSaveError(undefined);
            }} />
              </StyledAmount>
            </StyledAlertRow>}
        </StyledCard>
        {save_error && <Text danger size="s" role="alert" id={error_id} m={{
        t: 2
      }}>
            {save_error}
          </Text>}
        <Button primary submit m={{
        t: 4
      }} disabled={!can_edit || is_saving || !is_changed} icon={is_saving ? 'loading' : 'save'}>
          Save
        </Button>
      </form>
    </Div>;
};
export default BillingAlerts;